import { afterNextRender, booleanAttribute, computed, DestroyRef, Directive, DoCheck, ElementRef, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LoadingIndicatorDirective } from '@rma/generic/ui/loading-indicator';
import { hasAccessibleName } from './accessible-name';
import {
  isIconOnlyAppearance,
  RmaButtonAppearance,
  RmaButtonBrand,
  rmaButtonClasses,
  RmaButtonClassOptions,
  RmaButtonShape,
  RmaButtonTone,
} from './button.model';
import { RMA_BUTTON_BRAND } from './button-brand';

// Set by Angular's own bootstrap; absent once the production optimiser has stripped it.
declare const ngDevMode: unknown;

function inDevMode(): boolean {
  return typeof ngDevMode !== 'undefined' && !!ngDevMode;
}

// Where a stripped `href` is parked while an anchor is disabled — see applyAnchorHref.
const HREF_STASH_ATTRIBUTE = 'data-rma-button-href';

function subtractClasses(all: string, remove: string): string {
  const removeSet = new Set(remove.split(/\s+/));

  return all
    .split(/\s+/)
    .filter((name) => name.length > 0 && !removeSet.has(name))
    .join(' ');
}

/**
 * Brand-aware button surface. The host stays whatever element the author wrote — a `<button>`
 * is never coerced to an `<a>` and vice versa — and all paint comes from Tailwind utility
 * classes composed per brand in `button.model.ts`. Brand comes from `RMA_BUTTON_BRAND` (each
 * app provides its own) and the `brand` input overrides it per usage, so a dashboard-styled
 * button can sit inside a public page.
 *
 * The `disabled` state is reflected onto the DOM imperatively rather than through host
 * bindings. Another directive on the same host (`rmaLoadingIndicator`) also disables the
 * button, and an `[attr.disabled]` binding writing `null` would take a boolean attribute back
 * off the element and silently re-enable it. Which of the two owns that attribute is decided
 * by which directives are present, never by reading the DOM — see `applyNativeDisabled`.
 *
 * That work runs in `ngDoCheck`, not in an `effect`. Two reasons, and both are load-bearing:
 * a directive's pre-order hooks run after the update pass has written its own host element's
 * bindings, so `[href]="url"` is on the DOM by the time it is read; and running every pass
 * means a binding that writes `href` again while the anchor is disabled is caught and stripped
 * on that same pass, rather than quietly restoring navigation.
 *
 * Nor in `afterEveryRender`, for a third reason: render callbacks run on browser platforms only.
 * A server-rendered disabled anchor would keep its `href` and stay navigable until hydration
 * completed — the precise failure the `href` stash below exists to prevent. The accessible-name
 * check has no such constraint and does use `afterNextRender`.
 */
@Directive({
  selector: 'button[rmaButton], a[rmaButton]',
  host: {
    '[class]': 'hostClasses()',
    '[attr.aria-disabled]': 'ariaDisabled()',
  },
})
export class RmaButtonDirective implements DoCheck {
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  // Optional and self-scoped: only a routerLink on this very element can contend for `href`.
  private readonly routerLink = inject(RouterLink, { optional: true, self: true });
  // Likewise for the native `disabled` attribute — see applyNativeDisabled.
  private readonly loadingIndicator = inject(LoadingIndicatorDirective, { optional: true, self: true });

  private readonly defaultBrand = inject(RMA_BUTTON_BRAND);

  private readonly isAnchor = this.host.tagName.toLowerCase() === 'a';

  protected readonly hostClasses = computed(() => {
    const options: RmaButtonClassOptions = {
      brand: this.brand() ?? this.defaultBrand,
      appearance: this.rmaButton(),
      tone: this.tone(),
      shape: this.shape(),
      fullWidth: this.fullWidth(),
      raised: this.raised(),
    };

    // Author classes are captured on the first computation, when the host still carries only
    // what the author (or the server) put there — this binding has not written yet. Under
    // hydration the markup holds the server's full merged output, so the current variant
    // output is subtracted to recover the author's own classes rather than adopting stale
    // variant classes that would then outrank a later input change in the merge.
    this.authorClasses ??= subtractClasses(this.host.className, rmaButtonClasses(options));

    return rmaButtonClasses({ ...options, class: this.authorClasses });
  });

  // Anchors have no native disabled state, so their whole disabled contract is carried by ARIA
  // plus the DOM edits below. A button already exposes it natively and does not need both.
  protected readonly ariaDisabled = computed(() => (this.isAnchor && this.disabled() ? 'true' : null));

  public readonly rmaButton = input.required<RmaButtonAppearance>();

  /** Overrides the app-level `RMA_BUTTON_BRAND` for this one button. */
  public readonly brand = input<RmaButtonBrand | undefined>(undefined);

  public readonly tone = input<RmaButtonTone>('neutral');

  public readonly shape = input<RmaButtonShape>('default');

  public readonly fullWidth = input(false, { transform: booleanAttribute });

  public readonly raised = input(false, { transform: booleanAttribute });

  public readonly disabled = input(false, { transform: booleanAttribute });

  // The host's own classes, captured once by the hostClasses computation — see the comment
  // there for why the capture is lazy and subtractive.
  private authorClasses: string | undefined = undefined;

  // Captured at the moment of disabling, never at construction: the author's value may arrive
  // from a binding (`[href]="url"`, `[attr.tabindex]="n"`) that has not written yet when the
  // directive is built. `undefined` means "nothing captured"; for tabindex `null` is a real
  // captured value, meaning the author supplied none and the attribute must go on re-enable.
  private capturedHref: string | undefined = undefined;

  private capturedTabIndex: string | null | undefined = undefined;

  // The last value this directive wrote to the native `disabled` attribute, or `undefined` if it
  // has not written yet. Deliberately its own memory rather than a read of the DOM: under
  // hydration the DOM is the server's output, and a directive that infers ownership from it
  // would decline to own an attribute it is in fact responsible for, and never take it off.
  private nativeDisabledWritten: boolean | undefined = undefined;

  constructor() {
    // Registered from the constructor on purpose. Angular instantiates every directive on an
    // element before it registers any of their host listeners, so this listener always runs
    // ahead of RouterLink's own click handler and can stop it navigating — without depending on
    // which order the two directives happen to be matched in.
    this.host.addEventListener('click', this.suppressWhenDisabled, true);
    this.host.addEventListener('keydown', this.suppressEnterWhenDisabled, true);
    this.destroyRef.onDestroy(() => {
      this.host.removeEventListener('click', this.suppressWhenDisabled, true);
      this.host.removeEventListener('keydown', this.suppressEnterWhenDisabled, true);
    });

    // A DOM read once the first render has landed, so an icon button named by its own projected
    // content is seen as named. Being browser-only suits a check whose whole job is to fail a
    // developer's page rather than a server render.
    afterNextRender({ read: () => this.assertAccessibleName() });
  }

  public ngDoCheck(): void {
    const disabled = this.disabled();

    this.applyNativeDisabled(disabled);
    this.applyTabIndex(disabled);
    this.applyAnchorHref(disabled);
  }

  private assertAccessibleName(): void {
    const appearance = this.rmaButton();

    if (!inDevMode() || !isIconOnlyAppearance(appearance) || hasAccessibleName(this.host)) {
      return;
    }

    throw new Error(
      `rmaButton="${appearance}" resolves no accessible name. Give the host visible text, an aria-label, or an aria-labelledby.`,
    );
  }

  /**
   * Reflects `disabled` onto a `<button>` host — but only when this directive is the one
   * responsible for it.
   *
   * When `rmaLoadingIndicator` shares the host, it owns the attribute outright and this
   * directive writes nothing. That delegation rests on one invariant, which holds because both
   * sides are written to hold it rather than by luck:
   *
   *   1. Angular routes a `disabled` binding — and a static `disabled` attribute — to every
   *      directive on the element that declares that input, so both see the same raw value.
   *   2. Both coerce it with `booleanAttribute`, so both resolve that raw value identically.
   *      A bare attribute is `''`, which is falsy but coerces to `true`; `disabled="false"` is
   *      truthy but coerces to `false`. Without the transform on *both*, the two disagree on
   *      exactly those forms.
   *
   * Given 1 and 2, `disabled || loading` on the loading indicator is the true union of both
   * directives' state, so it is the correct single writer. Writing here as well would fight it:
   * this directive's `disabled` going false mid-load would strip an attribute the indicator
   * still wants, and its host binding would not put it back, because from its side the computed
   * value never changed.
   *
   * If the `booleanAttribute` transform is ever removed from `LoadingIndicatorDirective.disabled`
   * the invariant fails and this delegation silently under-disables the host. That transform is
   * load-bearing for this method, not decoration.
   *
   * Where it is absent, the write is unconditional against remembered state rather than against
   * the DOM, which keeps it correct under hydration and free of order-of-arrival guesswork.
   */
  private applyNativeDisabled(disabled: boolean): void {
    if (this.isAnchor || this.loadingIndicator !== null) {
      return;
    }

    if (this.nativeDisabledWritten === disabled) {
      return;
    }

    this.nativeDisabledWritten = disabled;

    if (disabled) {
      this.host.setAttribute('disabled', '');
    } else {
      this.host.removeAttribute('disabled');
    }
  }

  /**
   * Removing `href` already drops an anchor out of the tab order, but an author-supplied
   * tabindex would put it straight back. The author's value is restored verbatim on re-enable,
   * including its absence.
   */
  private applyTabIndex(disabled: boolean): void {
    if (!this.isAnchor) {
      return;
    }

    if (disabled) {
      const current = this.host.getAttribute('tabindex');

      // Re-reading each pass rather than once: a bound tabindex can change while disabled, and
      // the value restored on re-enable should be the latest one, not the first seen.
      if (current !== '-1') {
        this.capturedTabIndex = current;
        this.host.setAttribute('tabindex', '-1');
      }

      return;
    }

    if (this.capturedTabIndex === undefined) {
      return;
    }

    const restore = this.capturedTabIndex;
    this.capturedTabIndex = undefined;

    if (restore === null) {
      this.host.removeAttribute('tabindex');
    } else {
      this.host.setAttribute('tabindex', restore);
    }
  }

  /**
   * A disabled anchor must not be navigable, so the `href` comes off rather than being covered
   * by `pointer-events`. RouterLink owns `[attr.href]` through a linked signal, so going via its
   * public `href` setter is the only edit that survives the next change-detection pass.
   *
   * On the plain-`href` path the captured value is also parked in a `data-` attribute, because
   * `capturedHref` alone does not survive server-side rendering. A server instance strips the
   * attribute and holds the value in memory; the client then hydrates a fresh instance over
   * markup that no longer carries the `href`, and — since a static attribute lives in the
   * template's create-mode instructions, which hydration skips — nothing re-applies it. Without
   * the stash the anchor is left permanently unnavigable, and silently: the DOM *tree* matches
   * either way, so hydration raises nothing. The stash travels inside the serialised markup, so
   * the client can recover what its server counterpart captured. It is removed on restore.
   *
   * RouterLink needs no stash: its href derives from a linked signal over its own url tree,
   * which a fresh client instance recomputes from the binding — the value is regenerated rather
   * than inherited. A bound `[href]` self-heals for the same reason, the binding having written
   * before this hook runs; stashing it anyway is harmless, and the live attribute is always
   * re-read in preference to the stash, so the two cannot drift apart.
   */
  private applyAnchorHref(disabled: boolean): void {
    if (!this.isAnchor) {
      return;
    }

    const link = this.routerLink;

    if (disabled) {
      const current = link === null ? this.host.getAttribute('href') : link.href;

      if (current === null) {
        return;
      }

      this.capturedHref = current;

      if (link === null) {
        this.host.setAttribute(HREF_STASH_ATTRIBUTE, current);
        this.host.removeAttribute('href');
      } else {
        link.href = null;
      }

      return;
    }

    // This instance's own capture first; the stash is the fallback for a client instance that
    // never saw the attribute because its server counterpart had already taken it off.
    const restore = this.capturedHref ?? this.host.getAttribute(HREF_STASH_ATTRIBUTE) ?? undefined;

    if (restore === undefined) {
      return;
    }

    this.capturedHref = undefined;

    if (link === null) {
      this.host.removeAttribute(HREF_STASH_ATTRIBUTE);
      this.host.setAttribute('href', restore);
    } else {
      link.href = restore;
    }
  }

  // Stored as fields so the same reference can be removed on destroy. Field initialisers run
  // before the constructor body, so both exist by the time the listeners are registered.
  private readonly suppressWhenDisabled = (event: Event): void => {
    if (!this.disabled()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly suppressEnterWhenDisabled = (event: Event): void => {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    if (!this.disabled() || (event as KeyboardEvent).key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  };
}
